import { compareGrantPrecedence, hasRevocationHandle } from '@marva/shared/sale-grant-precedence'
import type { ActiveDiscount, Sale } from '../../services/api'

/**
 * How a detected Shopify discount is described to the merchant, and what she is allowed
 * to decide about it (O1 item 3 §3.1/§3.8). Kept out of the component so the rules are
 * testable on their own — each one exists because getting it wrong hands a shopper an
 * offer they cannot use.
 */

/**
 * Shopify's own phrasing of the terms, preferred over anything we compose from
 * valueType + value. It is both more accurate and more readable, and composing was
 * unsafe: a discount whose terms could not be read has no `value`, and the old fallback
 * rendered "$undefined off".
 */
export const discountTerms = (d: ActiveDiscount): string =>
  d.shortSummary ||
  d.summary ||
  (d.valueType === 'percentage' && d.value != null
    ? `${d.value}% off`
    : d.valueType === 'fixed_amount' && d.value != null
      ? `$${d.value} off`
      : 'terms unavailable')

/**
 * What a shopper would have to do to get it. Three states, not two: `requiresCode`
 * undefined is a row written before discounts were hydrated, and calling that "applies
 * automatically" asserts something we do not know.
 */
export const discountRedemption = (d: ActiveDiscount): string =>
  d.requiresCode === false ? 'applies automatically' : d.code ? `code ${d.code}` : 'code unknown'

export const discountExpiry = (d: ActiveDiscount): string =>
  d.expiresAt ? `expires ${new Date(d.expiresAt).toLocaleDateString()}` : 'no expiry'

/**
 * The join between a sale row and a discount row is the discount's OWN id. The stored
 * field is called `linkedPriceRuleId` for history, but `applyMentionRights` matches it
 * against `discountId` and `getActiveDiscount` keys by `sk = DISCOUNT#{discountId}`.
 * `priceRuleId` is a legacy alias of the same value; it is the fallback, not the source.
 */
export const discountKey = (d: ActiveDiscount): string => String(d.discountId || d.priceRuleId)

export type Speakability =
  | { kind: 'automatic' }
  | { kind: 'code' }
  | { kind: 'silent'; reason: string }

/**
 * Merchant-facing prose for a machine reason the API returned.
 *
 * The *verdict* is not ours (see `speakability` below). Turning `terms_incomplete` into
 * a sentence a merchant can act on is presentation, and presentation is what this file
 * is for. The keys mirror `speakability`'s vocabulary in `@marva/shared/active-sales`
 * (`not_live`, `terms_unreadable`, `terms_incomplete`, and whatever hydration recorded
 * as `undescribableReason`) plus the two the local fallback below still derives on its
 * own. An unrecognised code — one added server-side after this map was written — still
 * gets a true sentence rather than a blank or a raw identifier.
 */
const SPEAKABLE_REASON_WORDING: Record<string, string> = {
  terms_unreadable:
    "Shopify could not tell us this discount's terms, so Marva has no truthful way to describe it. " +
    'It still applies at checkout exactly as you set it up.',
  terms_incomplete:
    'Shopify told us some of this one\'s terms but not all of them — what it applies to, how much it takes off, or ' +
    'the spend it depends on. Marva would have to leave a piece out, so she stays quiet until Shopify sends the rest.',
  hydration_failed:
    "We could not read this discount's terms from Shopify, so Marva has no truthful way to describe it. " +
    'It still applies at checkout exactly as you set it up, and it will get a control here once Shopify sends them.',
  app_discount_opaque:
    'This one is run by an app or a Shopify Function, so its terms never reach us and Marva has no truthful way ' +
    'to describe it. It still applies at checkout exactly as you set it up — shoppers see it as the price on the item.',
  not_live: 'Shopify tells us this one is not running right now, so there is nothing for Marva to raise about it.',
  // Derived locally, not by the API: the fallback below still answers for a response
  // that carries no verdict at all.
  redemption_unknown:
    'We do not yet know whether this one needs a code, so Marva stays quiet about it. ' +
    'It will get a control here once Shopify tells us.',
  code_missing:
    'Shopify has not given us the code for this one, so a shopper would have no way to redeem it. ' +
    'Marva will not raise it until Shopify sends us the code.',
}

const UNSPEAKABLE_FALLBACK =
  'Marva has no truthful way to describe this one, so she stays quiet about it. ' +
  'It still applies at checkout exactly as you set it up.'

export const speakableReasonWording = (reason: string | null | undefined): string =>
  (reason ? SPEAKABLE_REASON_WORDING[reason] : undefined) || UNSPEAKABLE_FALLBACK

/**
 * What this panel is ALLOWED to decide about a row — and, before that, whether the agent
 * may say anything about it at all.
 *
 * ⚠️ THE VERDICT IS THE API'S, not ours. `speakable` is computed server-side from
 * `isStateable` (`@marva/shared/active-sales`), which is the same predicate that gates
 * the agent and that `POST /sales` refuses a grant against. This function used to derive
 * its own answer from `describable !== false`, which is strictly weaker: `isStateable`
 * also demands a known scope, a renderable value (with a currency, for money) and a
 * renderable minimum. So the panel offered an actionable toggle on rows the grant
 * endpoint 400s, and badged "Always speakable" on rows the agent silently withholds.
 * Two predicates for one question drift; the fix is to have one.
 *
 * What remains local is the *shape of the control*, which is a presentation question the
 * verdict does not answer: a speakable automatic discount needs no toggle (no code
 * changes hands, so there is no margin decision to take), a speakable code discount does.
 */
export const speakability = (d: ActiveDiscount): Speakability => {
  if (d.speakable === false) return { kind: 'silent', reason: speakableReasonWording(d.speakableReason) }
  if (d.speakable === true) {
    if (d.requiresCode === false) return { kind: 'automatic' }
    if (d.requiresCode === true) return { kind: 'code' }
    // Speakable but we cannot tell how it is redeemed. `isStateable` rejects that
    // combination, so it should not reach us — and if it ever does, guessing "automatic"
    // is how a shopper is handed an offer they cannot use.
    return derivedSpeakability(d)
  }
  // A response older than the field. Falling through to "unspeakable" would blank every
  // control in the panel on a stale deploy; falling through to "speakable" would restate
  // the bug. So: answer from what the row itself carries, which is what the panel did
  // before the API had a verdict, and no more.
  return derivedSpeakability(d)
}

/**
 * The pre-verdict local derivation, kept ONLY as the fallback above (and as its own unit
 * under test). It is deliberately not exported for general use: anywhere `speakable` is
 * available, `speakable` wins.
 */
export const derivedSpeakability = (d: ActiveDiscount): Speakability => {
  if (d.describable === false) {
    return { kind: 'silent', reason: speakableReasonWording('terms_unreadable') }
  }
  // A genuine third state. Not a synonym for "automatic": this row predates discount
  // hydration, and guessing is how a shopper is handed an offer they cannot use.
  if (d.requiresCode === undefined) {
    return { kind: 'silent', reason: speakableReasonWording('redemption_unknown') }
  }
  if (!d.requiresCode) return { kind: 'automatic' }
  // Announced but unredeemable is still nothing a shopper can act on: the code lives on a
  // separate Shopify resource for legacy price rules, and hydration can simply fail.
  if (!d.code) {
    return { kind: 'silent', reason: speakableReasonWording('code_missing') }
  }
  return { kind: 'code' }
}

/**
 * Does a linked annotation actually grant mention rights *right now*?
 *
 * ⚠️ This MIRRORS `fetchLiveAnnouncements` in `@marva/shared/active-sales`, which is the
 * only reader that decides whether Marva may raise the discount. The panel used to treat
 * the mere existence of a row keyed on `linkedPriceRuleId` as a live grant, so an ended
 * annotation — or one the merchant scheduled to start next Monday — rendered as a switch
 * in the on position: the panel telling her Marva may raise a discount when she may not.
 * If the reader's rules change, change these with them; two filters that disagree are
 * worse than one.
 *
 * The reader's three rules, in its order:
 *   1. `status === 'active'` — `POST /sales/{id}/end` writes `ended`, and nothing else
 *      is ever written. An ended row is not a grant.
 *   2. `endsAt` is ignored for a `shopify_detected` row and enforced for every other
 *      provenance. A detected annotation's liveness IS the discount's — the reader pairs
 *      only against discounts that survived `isLiveDiscount` — and rows written by the
 *      first cut of phase 5 snapshotted a since-extended `endsAt` that would otherwise
 *      revoke a right the merchant never withdrew.
 *   3. `startsAt <= now` — a genuine merchant boundary ("not until Monday"), not a copy
 *      of a Shopify fact, so it is enforced on both provenances.
 *
 * Rule 3 is the one state worth naming rather than collapsing into "no grant": the
 * merchant chose it, and showing her nothing would be its own small lie.
 */
export type GrantStanding =
  | { kind: 'none' }
  | { kind: 'active' }
  | { kind: 'scheduled'; startsAt: string }

export const grantStanding = (sale: Sale | undefined, nowIso: string): GrantStanding => {
  if (!sale) return { kind: 'none' }
  if (sale.status !== 'active') return { kind: 'none' }
  if (sale.source !== 'shopify_detected' && sale.endsAt && sale.endsAt <= nowIso) return { kind: 'none' }
  if (sale.startsAt && sale.startsAt > nowIso) return { kind: 'scheduled', startsAt: sale.startsAt }
  return { kind: 'active' }
}

/**
 * What the panel is entitled to SAY about a discount's mention rights, which is a
 * different question from what the rows in hand happen to show.
 *
 * `grantStanding` answers from a row. This answers from a row **and the read that
 * produced it**, because `none` is the one verdict that needs the whole list behind it:
 * it is a claim that nothing anywhere grants this discount. A read that never landed
 * cannot support that claim, and neither can one that stopped at its row cap — the grant
 * that would contradict it may simply sit past the cap. Rendering it as an off switch
 * states "you have allowed nothing here" and offers a POST as the remedy; the POST comes
 * back `exists`, the next capped read omits the row again, and the `saleId` needed to
 * REVOKE never arrives. The merchant is then told she has not granted a right she cannot
 * take back.
 *
 * `active` and `scheduled` need no such completeness: a row in hand is a row in hand, and
 * it carries the `saleId` that makes the control reversible. So a short read narrows the
 * panel to the grants it can prove and says "not checked" about the rest, rather than
 * turning its own absences into the merchant's decisions.
 */
export type MentionRights = GrantStanding | { kind: 'unknown' }

export const mentionRights = (
  sale: Sale | undefined,
  read: { landed: boolean; complete: boolean },
  nowIso: string
): MentionRights => {
  if (!read.landed) return { kind: 'unknown' }
  const standing = grantStanding(sale, nowIso)
  if (!read.complete && standing.kind === 'none') return { kind: 'unknown' }
  return standing
}

/**
 * One discount, several annotations — which one speaks for it?
 *
 * `GET /sales` filters nothing, so a discount can come back with more than one row
 * against it: the ordinary shape is an `ended` grant the merchant took back and an
 * `active` one she made afterwards, and phase 5 keeps supporting those legacy duplicate
 * sets rather than migrating them away. The panel used to collapse them with a plain
 * `map.set()` per row, so the LAST row in whatever order the API happened to return won
 * — arbitrarily. Pick the `ended` sibling and the merchant is shown an OFF switch for a
 * grant that is genuinely in force; clicking it POSTs, `POST /sales` is idempotent and
 * answers `exists`, the next read collapses to the same ended row, and the active row's
 * `saleId` — the only revocation handle there is — is never exposed. She is told she has
 * allowed nothing, and refused the control that would take it back.
 *
 * The reader has no such problem: `fetchLiveAnnouncements` filters to live grants BEFORE
 * it builds its map, so every row it collapses is already a grant and any of them is a
 * true answer. The panel has to reach the same verdict while still holding the rows the
 * reader threw away (it must show her the scheduled grant, and the ended one she can
 * re-grant). So it reduces by STANDING first, and by the shared tie-break after:
 *
 *   1. Highest standing wins — `active` beats `scheduled` beats `none`. This is the rule
 *      that makes the panel agree with `fetchLiveAnnouncements` by construction: if any
 *      row would survive the reader's three filters, the winner is one of those rows.
 *      The reader needs no equivalent — it has already discarded everything else.
 *   2. Within a standing, `compareGrantPrecedence` decides, and it is IMPORTED rather than
 *      mirrored: the deterministic `SALE#{discountId}` row, then a row with a revocation
 *      handle, then the newest `startsAt`, then the lower `saleId`.
 *
 * ⚠️ Rule 2 used to be a local copy that resolved the OPPOSITE way — the panel preferred
 * the lower `saleId` on equal timestamps while the agent's query-then-overwrite path
 * usually ended on the higher key — so the merchant could save her wording to a row the
 * agent never read. One rule, one file, imported by both; see
 * `@marva/shared/sale-grant-precedence` for the order and why it is dependency-free.
 */
const STANDING_RANK: Record<GrantStanding['kind'], number> = { none: 0, scheduled: 1, active: 2 }

export type EffectiveGrant = { standing: GrantStanding; sale?: Sale }

/**
 * The single annotation that decides this discount's mention rights, reduced from every
 * row the read returned against it. `sale` is the winner and carries the `saleId` the
 * revoke and the wording editor act on.
 */
export const effectiveGrant = (candidates: Sale[] | undefined, nowIso: string): EffectiveGrant => {
  let best: EffectiveGrant = { standing: { kind: 'none' } }
  for (const candidate of candidates || []) {
    const standing = grantStanding(candidate, nowIso)
    const rank = STANDING_RANK[standing.kind]
    const bestRank = STANDING_RANK[best.standing.kind]
    if (!best.sale || rank > bestRank) {
      best = { standing, sale: candidate }
      continue
    }
    if (rank < bestRank) continue
    if (compareGrantPrecedence(candidate, best.sale) < 0) best = { standing, sale: candidate }
  }
  return best
}

/**
 * `effectiveGrant` plus the read that produced it — the list-shaped counterpart of
 * `mentionRights`, and the only one the panel should call.
 *
 * One rule lives here rather than in the reduction: a live standing whose winning row
 * carries no `saleId` is reported as `unknown`, not as an on switch. An ON switch is an
 * offer to turn it off, and a row we cannot address is a switch that would silently do
 * nothing — the same "states a permission it cannot act on" failure as the arbitrary
 * collapse above, one step further in. Nothing writes such a row (`saleId` is the row's
 * own key); this is the floor under that assumption, not an expected state.
 */
export const effectiveMentionRights = (
  candidates: Sale[] | undefined,
  read: { landed: boolean; complete: boolean },
  nowIso: string
): { rights: MentionRights; sale?: Sale } => {
  const { sale } = effectiveGrant(candidates, nowIso)
  // Deliberately re-asked through `mentionRights` rather than re-deriving from the
  // standing above: the read-completeness rules are decided in exactly one place, and
  // `grantStanding(sale)` is by construction the standing that won the reduction.
  const rights = mentionRights(sale, read, nowIso)
  // `hasRevocationHandle` is the shared rule's own second criterion, imported rather than
  // re-asked locally: whether a row can be revoked must mean one thing in both places.
  if ((rights.kind === 'active' || rights.kind === 'scheduled') && (!sale || !hasRevocationHandle(sale))) {
    return { rights: { kind: 'unknown' } }
  }
  return { rights, sale }
}

/**
 * Every annotation `GET /sales` returned, grouped by the discount it links to — see
 * `discountKey()` for why `linkedPriceRuleId` holds the discount's own id. Grouping
 * rather than collapsing is the point: the reduction above needs all the rows, and
 * keeping only the live ones would lose the scheduled row the panel has to show her and
 * the ended one she can re-grant.
 */
export const salesByDiscountKey = (sales: Sale[]): Map<string, Sale[]> => {
  const grouped = new Map<string, Sale[]>()
  for (const sale of sales) {
    if (!sale.linkedPriceRuleId) continue
    const key = String(sale.linkedPriceRuleId)
    const existing = grouped.get(key)
    if (existing) existing.push(sale)
    else grouped.set(key, [sale])
  }
  return grouped
}
